/**
 * Display copy for household roles and capabilities.
 *
 * ONLY copy lives here. Which permissions a role actually grants comes from the
 * server (`roleCatalog` on the co-owners response, resolved from
 * `HOUSEHOLD_ROLE_PERMISSIONS` in `@ordo/schema`) so the picker can never
 * advertise a grant the server won't honor.
 */

import type { HouseholdPermission, HouseholdRole } from '@/api/co-owners'

export const HOUSEHOLD_ROLE_LABEL: Record<HouseholdRole, string> = {
  PRIMARY_GUARDIAN: 'Primary Guardian',
  GUARDIAN: 'Guardian',
}

/**
 * Tag-sized labels for dense rows. "Primary Guardian" is 16 characters — as a
 * pill beside a name it outweighed the name itself. In a list captioned "Adults
 * on this household", "Primary" is unambiguous.
 *
 * Use `HOUSEHOLD_ROLE_LABEL` anywhere the role is the subject rather than an
 * annotation: the role picker, confirms, toasts.
 */
export const HOUSEHOLD_ROLE_TAG_LABEL: Record<HouseholdRole, string> = {
  PRIMARY_GUARDIAN: 'Primary',
  GUARDIAN: 'Guardian',
}

export const HOUSEHOLD_ROLE_DESCRIPTION: Record<HouseholdRole, string> = {
  PRIMARY_GUARDIAN: 'Full access, including who else can be primary guardian.',
  GUARDIAN: 'Can do everything except change who the primary guardian is.',
}

/** Short, plain-language name for each capability, for the permission lists. */
export const HOUSEHOLD_PERMISSION_LABEL: Record<HouseholdPermission, string> = {
  SEE_BALANCE: 'See the household balance',
  PLACE_ORDERS: 'Place and change orders',
  MANAGE_STUDENTS: 'Add and edit students',
  TOP_UP: 'Add funds',
  MANAGE_PAYMENT_METHODS: 'Manage payment methods',
  MANAGE_MEMBERS: 'Invite and manage members',
  MANAGE_PRIMARY_GUARDIAN: 'Change who the primary guardian is',
}

export function householdRoleLabel(role: HouseholdRole | null): string {
  return role === null ? 'Guardian' : HOUSEHOLD_ROLE_LABEL[role]
}
